import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { Place, PendingActions, Verified, Block, Inventory2, Speed } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { StatCard } from '../../../components/StatCard';
import { MiniBarChart } from '../../../components/MiniBarChart';
import { EmptyState } from '../../../components/EmptyState';
import { siteDashboardApi } from '../../../api/siteOnboarding.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function SiteOnboardingDashboardPage() {
  const { data } = useQuery({ queryKey: ['site-onboarding-dashboard'], queryFn: () => siteDashboardApi.dashboard() });
  const k = data?.kpis ?? {};
  const c = data?.charts ?? {};

  return (
    <>
      <PageHeader title="Site Dashboard" subtitle="Site lifecycle pipeline, delays and SLA performance." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Sites" value={n(k.totalSites)} icon={<Place />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active Sites" value={n(k.activeSites)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Delayed Sites" value={n(k.delayedSites)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Survey Pending" value={n(k.surveyPending)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0d9488" label="Material Pending" value={n(k.materialPending)} icon={<Inventory2 />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0284c7" label="Handover Pending" value={n(k.handoverPending)} icon={<PendingActions />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4}><StatCard color="#16a34a" label="Completed Sites" value={n(k.completedSites)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#1e40af" label="Site SLA Performance" value={`${n(k.siteSlaPerformance)}%`} icon={<Speed />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#d97706" label="Delayed Sites" value={n(k.delayedSites)} icon={<Block />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Status" data={c.sitesByStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Type" data={c.sitesByType ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Project Type" data={c.sitesByProjectType ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Priority" data={c.sitesByPriority ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Region" data={c.sitesByRegion ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Sites by Charger Type" data={c.sitesByChargerType ?? []} /></Grid>

        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Recent Sites" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.recentSites.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Site ID</TableCell><TableCell>Site</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.recentSites.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.siteName)}</TableCell><TableCell><Chip size="small" label={String(r.status).replace(/_/g, ' ')} /></TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Most Delayed Sites" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.delayedSites.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Site ID</TableCell><TableCell>Site</TableCell><TableCell>Delay</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.delayedSites.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.siteName)}</TableCell><TableCell>{String(r.delayDays)}d</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
